import React, { useEffect, useState } from 'react';
import './filter-modal.scss';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Box from '@mui/system/Box';
import { TouchableOpacity } from '../../common';
import { Divider } from '../divider';
import { DividerColorVariant } from '../../constants';
import { CommonButton } from '../button';
import {
  applyFiltersByRoute,
  clearAppliedFilters,
  clearSelectedTimeRange,
  DateRangeState,
  dispatch,
  FilterRoute,
  updateSelectedTimeRange,
} from '@zonofi/zono-money-store';
import { FilterLeftSection } from './filter-left-section';
import { FilterRightSection } from './filter-right-section';
import { useInitializeFilters, useUrlDateRange } from '../../hooks';
import { RELATIVE_TIME_RANGE_ROUTES } from './defined-filter';
import { RelativeTimeRange } from '../../types';

interface FilterModalInterface {
  onClose: () => void;
  open: boolean;
  route: FilterRoute;
  filterPage?: string;
  dateSelectionMode?: 'date' | 'dateRange' | undefined;
  disableDateFilter?: boolean;
}

export const FilterModal: React.FC<FilterModalInterface> = ({
  open,
  onClose,
  route,
  filterPage,
  dateSelectionMode = 'dateRange',
  disableDateFilter = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dateRangeSelection, setDateRangeSelection] =
    useState<DateRangeState | null>(null);
  const [localTimeRange, setLocalTimeRange] =
    useState<RelativeTimeRange | null>(null);

  const { filters, onUpdateFilter, hasSelectedFilters } = useInitializeFilters({
    route,
    open,
  });

  const { updateRange } = useUrlDateRange({
    disableDefaultDateRange: disableDateFilter,
    route,
  });

  const isTimeRangeMode = route
    ? RELATIVE_TIME_RANGE_ROUTES.includes(route)
    : false;

  const handleApply = () => {
    if (isTimeRangeMode) {
      if (localTimeRange) {
        dispatch(updateSelectedTimeRange(localTimeRange));
      }
    } else if (dateRangeSelection?.startDate && dateRangeSelection?.endDate) {
      updateRange(dateRangeSelection);
    } else {
      dispatch(applyFiltersByRoute({ route }));
    }
    onClose();
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [route]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{ className: 'filter-modal__drawer' }}
    >
      <Box className="filter-modal__container">
        {/* HEADER */}
        <div className="filter-modal__header">
          <TouchableOpacity onPress={onClose}>
            <span className="filter-modal__heading-close-text font-semibold">
              Close
            </span>
          </TouchableOpacity>

          <span className="filter-modal__heading-text font-semibold">
            Filter
          </span>

          <TouchableOpacity
            onPress={() => {
              if (isTimeRangeMode) {
                setLocalTimeRange(null);
                dispatch(clearSelectedTimeRange());
              }
              dispatch(clearAppliedFilters({ route }));
            }}
          >
            <span className="filter-modal__heading-subtext font-semibold">
              Clear All
            </span>
          </TouchableOpacity>
        </div>

        <Divider colorVariant={DividerColorVariant.SECONDARY} />

        {/* SCROLLABLE BODY */}
        <div className="filter-modal__body">
          <div className="filter-modal__container-view">
            {/* LEFT FILTER LIST */}
            <FilterLeftSection
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              filters={filters}
              route={route}
              filterPage={filterPage}
            />

            <div className="filter-modal-left-box-divider" />

            {/* RIGHT FILTER OPTIONS */}
            <FilterRightSection
              currentIndex={currentIndex}
              filters={filters}
              onUpdateFilter={onUpdateFilter}
              dateRangeSelection={dateRangeSelection}
              setDateRangeSelection={setDateRangeSelection}
              route={route}
              dateSelectionMode={dateSelectionMode}
              localTimeRange={localTimeRange}
              setLocalTimeRange={setLocalTimeRange}
            />
          </div>
        </div>

        {/* FIXED APPLY BUTTON */}
        <div className="apply-button-view">
          <CommonButton
            text="Apply Filter"
            onPress={handleApply}
            spinnerStyle="apply-button-view-spinner"
            buttonStyle="apply-button-view-style primary-bg"
            loading={false}
            spinnerColor="white"
            disabled={
              !(
                hasSelectedFilters ||
                localTimeRange ||
                (dateRangeSelection?.startDate && dateRangeSelection?.endDate)
              )
            }
          />
        </div>
      </Box>
    </SwipeableDrawer>
  );
};
